import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { SelectField } from './fields';
import EditHistoryLink from './EditHistoryLink';
import type { CustomDrawerProps } from './TablemaintPage';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';
import ratezoneMileageService, { MileageTier, ZoneOption } from '../../services/ratezonemileage.service';

/**
 * Create/edit drawer for Rate Zone Mileage. The flat table is edited grouped by
 * rate zone: one drawer holds the zone + all its mileage tiers (Miles / Rate Type
 * / Rate), saved together. New records pick an as-yet-unused zone; on edit the
 * zone is locked. (V1 editratezonemileage, modernized off the inline grid.)
 */
const RATE_TYPES = ['', 'FLAT', 'PER MILE'];

const s = (v: any): string => (v == null ? '' : String(v));
let _tmpId = -1;
const tmpId = () => _tmpId--;

const RatezoneMileageEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const isNew = !row;
  const initialZone = isNew ? '' : s((row as TablemaintRow).ratezone);

  const [ratezone, setRatezone] = useState(initialZone);
  const [tiers, setTiers] = useState<MileageTier[]>([]);
  const [deletes, setDeletes] = useState<number[]>([]);
  const [zoneOpts, setZoneOpts] = useState<ZoneOption[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    if (isNew) {
      ratezoneMileageService.unusedZones(ctrl.signal).then(setZoneOpts).catch(() => {});
    } else {
      ratezoneMileageService.getLines(initialZone, ctrl.signal).then((r) => setTiers(r.lines)).catch(() => {});
    }
    return () => ctrl.abort();
  }, [isNew, initialZone]);

  const setTier = (recno: number, patch: Partial<MileageTier>) =>
    setTiers((p) => p.map((t) => (t.recno === recno ? { ...t, ...patch } : t)));
  const addTier = () =>
    setTiers((p) => [...p, { recno: tmpId(), miles: '', ratetype: '', mileagerate: '' }]);
  const removeTier = (t: MileageTier) => {
    if (t.recno > 0) setDeletes((d) => [...d, t.recno]);
    setTiers((p) => p.filter((x) => x.recno !== t.recno));
  };

  const active = (t: MileageTier) => s(t.miles).trim() !== ''; // a tier counts once Miles is set

  const errors = useMemo(() => {
    const errs: string[] = [];
    if (isNew && !ratezone) errs.push('Rate Zone is required.');
    const seen = new Set<string>();
    tiers.forEach((t) => {
      if (!active(t)) return;
      const m = s(t.miles).trim();
      if (seen.has(m)) errs.push(`There can only be one entry per Miles (${m}).`);
      seen.add(m);
      if (!s(t.ratetype).trim()) errs.push(`Rate Type is required for ${m} miles.`);
      if (s(t.mileagerate).trim() === '') errs.push(`Rate is required for ${m} miles.`);
    });
    return Array.from(new Set(errs));
  }, [isNew, ratezone, tiers]);
  const banner = [...(showErrors ? errors : []), ...(saveError ? [saveError] : [])];

  useEffect(() => {
    if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [saveError, showErrors]);

  const handleSave = useCallback(async () => {
    if (errors.length) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    try {
      const lines = tiers.filter(active).map((t) => ({
        recno: t.recno > 0 ? t.recno : 0,
        miles: s(t.miles),
        ratetype: s(t.ratetype),
        mileagerate: s(t.mileagerate),
      }));
      await ratezoneMileageService.save({ ratezone, deletes, lines });
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save.');
    } finally {
      setIsSaving(false);
    }
  }, [errors, tiers, ratezone, deletes, onClose, onSaved]);

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> {isNew ? 'Create Rate Zone' : 'Save Changes'}</>)}
      </button>
    </div>
  );

  const inputCls =
    'w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm';

  return (
    <Drawer isOpen onClose={onClose} title={isNew ? 'Add Rate Zone Mileage' : `Rate Zone Mileage — ${initialZone}`}
      position="right" width="40rem" footer={footer}>
      <div className="space-y-5">
        <div ref={topRef} />
        {!isNew && (
          <div className="flex justify-end">
            {/* History is keyed by the rate zone (all tiers), not a single recno. */}
            <EditHistoryLink endpoint={ratezoneMileageService.historyEndpoint(initialZone)} label={initialZone} />
          </div>
        )}
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
              {banner.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {/* Rate Zone — pick an unused zone on create; locked on edit. */}
        {isNew ? (
          <SelectField label="Rate Zone" required value={ratezone} options={zoneOpts}
            disabled={isSaving} clearable={false} onChange={setRatezone} />
        ) : (
          <div>
            <label className="block text-sm font-medium text-muted mb-1">Rate Zone</label>
            <div className="px-3 py-2 min-h-[38px] text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg">
              {initialZone}
            </div>
          </div>
        )}

        {/* Mileage tiers */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Mileage Rates</legend>
          <div className="grid grid-cols-[1fr_1.2fr_1fr_auto] gap-2 items-center text-xs font-semibold text-muted px-1 mb-1">
            <div>Miles</div>
            <div>Rate Type</div>
            <div className="text-right">Rate</div>
            <div className="w-8 text-center">Del</div>
          </div>
          <div className="space-y-2">
            {tiers.map((t) => (
              <div key={t.recno} className="grid grid-cols-[1fr_1.2fr_1fr_auto] gap-2 items-center">
                <input type="number" step="1" min="0" disabled={isSaving} className={`${inputCls} text-right`}
                  value={s(t.miles)} onChange={(e) => setTier(t.recno, { miles: e.target.value })} />
                <select disabled={isSaving} className={inputCls}
                  value={s(t.ratetype)} onChange={(e) => setTier(t.recno, { ratetype: e.target.value })}>
                  {RATE_TYPES.map((rt) => <option key={rt} value={rt}>{rt || '—'}</option>)}
                </select>
                <input type="number" step="0.01" disabled={isSaving} className={`${inputCls} text-right`}
                  value={s(t.mileagerate)} onChange={(e) => setTier(t.recno, { mileagerate: e.target.value })} />
                <div className="w-8 flex justify-center">
                  <button type="button" onClick={() => removeTier(t)} disabled={isSaving}
                    className="p-1 text-red-500 hover:text-red-700" title="Remove tier">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {tiers.length === 0 && (
              <p className="text-sm text-gray-400 px-1">No mileage tiers yet — add one below.</p>
            )}
          </div>
          <button type="button" onClick={addTier} disabled={isSaving}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20">
            <PlusIcon className="w-4 h-4" /> Add Tier
          </button>
        </fieldset>
      </div>
    </Drawer>
  );
};

export default RatezoneMileageEditDrawer;
